import { useEffect, type ReactNode } from "react";
import { Footer } from "@/components/Footer";
import { Nav } from "@/components/Nav";
import { PilotDialog } from "@/components/PilotDialog";
import styles from "./about.module.css";

const PAGE_TITLE = "About HireOS";

/**
 * Who builds HireOS, for the operator deciding whether to trust it with their
 * phone line. It sits in the landing page's framed column and uses its type,
 * not the legal pages': the title, then each section as a row with its
 * heading on the left and the answer on the right, the way the FAQ is laid
 * out, and the footer under it like every other page.
 */
export default function AboutPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <>
      <Nav />

      <main>
        <div className="wrap">
          <header className={styles.head}>
            <h1 className={styles.title}>About HireOS</h1>
            <p className={styles.lede}>
              HireOS answers the calls and texts a hire desk can't get to. We build
              it for AV, event and equipment hire companies in New Zealand.
            </p>
          </header>
        </div>

        <Section title="Why we built it">
          <p className={styles.body}>
            My father runs{" "}
            <a href="https://accent-productions.co.nz" className={styles.link}>
              Accent Productions
            </a>
            , a small AV business in Wellington. I've seen what a missed call
            costs a hire desk: the job goes to whoever picked up. HireOS picks
            up when the team can't and keeps every call on the job it's about.
          </p>
        </Section>

        <Section title="Team">
          <div className={styles.team}>
            {TEAM.map((m) => (
              <Member key={m.name} {...m} />
            ))}
          </div>
        </Section>

        <Section title="Talk to us">
          <p className={styles.body}>
            Email{" "}
            <a href="mailto:hello@loony.io" className={styles.link}>
              hello@loony.io
            </a>
            .
          </p>
        </Section>
      </main>

      <Footer />

      <PilotDialog />
    </>
  );
}

/** An outbound link inside a bio line: the page's link look, opened away from us. */
function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className={styles.link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

type TeamMember = {
  name: string;
  role: string;
  /** Square, in public/. */
  photo: string;
  /**
   * One line per credential, each one the linked profile shows. A line may
   * carry links out to what it claims, so a reader checking us can follow
   * the claim rather than take it.
   */
  bio: ReactNode[];
  linkedin: string;
};

/**
 * Photo beside name, role and credentials. Every line in a bio is one the
 * linked profile shows, and the profile lists HireOS under Experience.
 */
const TEAM: TeamMember[] = [
  {
    name: "Kahu Hutton",
    role: "Founder (sole engineer)",
    photo: "/team-kahu.webp",
    bio: [
      <>
        Software/Product Engineer at{" "}
        <A href="https://nextwork.ai">NextWork</A>, developing AI tooling for a
        200K+ user platform. NextWork raised a US$4.45M (NZ$7.5M) seed round in
        March 2026, led by Shakti VC (
        <A href="https://www.finsmes.com/2026/03/nextwork-raises-4-45m-in-seed-funding.html">
          coverage
        </A>
        ).
      </>,
      <>
        Founded <A href="https://www.linkedin.com/company/kora-edu">KORA</A>,
        part of Google's 2025 startup program.
      </>,
      "Wrote flight software for Dawn Aerospace's CubeDrive and SatDrive satellites.",
      "Electrical engineering at the University of Canterbury.",
    ],
    linkedin: "https://www.linkedin.com/in/kahu-hutton/",
  },
];

function Member({ name, role, photo, bio, linkedin }: TeamMember) {
  return (
    <div className={styles.member}>
      <img
        className={styles.photo}
        src={photo}
        alt={name}
        width={640}
        height={640}
        loading="lazy"
      />
      <div className={styles.who}>
        <h3 className={styles.name}>{name}</h3>
        <p className={styles.role}>{role}</p>
      </div>
      <ul className={styles.bio}>
        {bio.map((line, i) => (
          // The list is static and never reorders, so its position is its identity.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>{line}</li>
        ))}
      </ul>
      <a
        href={linkedin}
        className={styles.memberLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        LinkedIn
      </a>
    </div>
  );
}

/**
 * Heading on the left, content on the right, tops lined up. Each one is its
 * own stretch of the framed column, under the landing page's rule.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="rule" />
      <div className="wrap">
        <section className={styles.row}>
          <h2 className={styles.heading}>{title}</h2>
          <div>{children}</div>
        </section>
      </div>
    </>
  );
}
